/**
 * Client-side spin-win sound synthesis via the Web Audio API.
 *
 * We generate the audio in-code so a fresh deploy has ZERO external
 * asset dependencies (no .mp3/.wav files to serve, no CDN dependency,
 * no autoplay-blocking surprises across mobile browsers).
 *
 * Each style is designed to feel distinct at a glance:
 *   • classic  → two-note ascending sine chime (warm, universal)
 *   • arcade   → three-note square-wave blip sequence (retro, energetic)
 *   • casino   → single triangle-wave bell tone with long exponential decay
 *   • none     → silent (no-op)
 *
 * Usage:
 *   import { playWinSound } from "@/lib/spinSound";
 *   playWinSound("arcade");
 *
 * The function is fire-and-forget — never throws, never returns a
 * promise you need to await. If the browser blocks autoplay (e.g.
 * `AudioContext` in "suspended" state before user gesture), we silently
 * skip. Playback of the win sound happens AFTER the user clicked spin,
 * so autoplay policies never actually kick in.
 */

const STYLES = ["classic", "arcade", "casino", "none"];

/** Guaranteed-valid style name, defaulting to "classic". */
export function normalizeSpinSound(style) {
  const s = (style || "").toLowerCase();
  return STYLES.includes(s) ? s : "classic";
}

/** Shared AudioContext — lazy-created so SSR / test envs don't crash. */
let _ctx = null;
function getContext() {
  if (typeof window === "undefined") return null;
  if (_ctx) return _ctx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  try {
    _ctx = new Ctx();
    return _ctx;
  } catch (err) {
    if (typeof console !== "undefined") {
      console.debug("[spinSound] AudioContext unavailable", err);
    }
    return null;
  }
}

/** Schedule a single note. All params are optional except freq. */
function scheduleNote(
  ctx,
  { freq, startAt = 0, duration = 0.15, type = "sine", gain = 0.2, decay = null },
) {
  const t0 = ctx.currentTime + startAt;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);

  // Snappy attack, gentle release — prevents clicks at note boundaries.
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.01);
  if (decay) {
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + duration + decay);
  } else {
    g.gain.linearRampToValueAtTime(0, t0 + duration);
  }

  osc.connect(g);
  g.connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + duration + (decay || 0.05));
}

/** Play the winning sound. Silent on style="none" or unsupported browsers. */
export function playWinSound(style) {
  const kind = normalizeSpinSound(style);
  if (kind === "none") return;
  const ctx = getContext();
  if (!ctx) return;
  // Resume if suspended (some browsers idle the context after inactivity).
  if (ctx.state === "suspended") {
    try {
      ctx.resume();
    } catch (err) {
      // Some browsers throw when resuming an already-closed context.
      // Silently swallow but log at debug level for diagnostics.
      if (typeof console !== "undefined") {
        console.debug("[spinSound] AudioContext resume failed", err);
      }
    }
  }

  try {
    if (kind === "classic") {
      // Two-note ascending sine chime: C5 → G5
      scheduleNote(ctx, { freq: 523.25, startAt: 0.00, duration: 0.22, type: "sine", gain: 0.22 });
      scheduleNote(ctx, { freq: 783.99, startAt: 0.16, duration: 0.36, type: "sine", gain: 0.24, decay: 0.15 });
    } else if (kind === "arcade") {
      // Three-note ascending triangle-wave blip: E5, G5, B5.
      // Triangle instead of square: same melody, roughly half the odd
      // harmonic content — noticeably softer on small phone speakers
      // while still reading as playful/energetic (per the "premium,
      // not casino" brief).
      scheduleNote(ctx, { freq: 659.25, startAt: 0.00, duration: 0.09, type: "triangle", gain: 0.18 });
      scheduleNote(ctx, { freq: 783.99, startAt: 0.10, duration: 0.09, type: "triangle", gain: 0.18 });
      scheduleNote(ctx, { freq: 987.77, startAt: 0.20, duration: 0.18, type: "triangle", gain: 0.20, decay: 0.10 });
    } else if (kind === "casino") {
      // Bell-like tone: triangle wave with long exponential decay
      scheduleNote(ctx, { freq: 1046.5, startAt: 0.00, duration: 0.05, type: "triangle", gain: 0.28, decay: 0.9 });
      scheduleNote(ctx, { freq: 1318.5, startAt: 0.00, duration: 0.05, type: "triangle", gain: 0.20, decay: 0.9 });
    }
  } catch (err) {
    // Never let a Web Audio hiccup break the popup animation.
    if (typeof console !== "undefined") {
      console.debug("[spinSound] synth failed for style", kind, err);
    }
  }
}

/** Preview button in the campaign builder uses the exact same synth. */
export function previewSpinSound(style) {
  playWinSound(style);
}
